import type { LucideIcon } from 'lucide-react';
import {
  Anchor,
  CalendarCheck,
  CalendarCog,
  CalendarRange,
  Check,
  CompassIcon,
  Flame,
  Footprints,
  ListChecks,
  Map,
  Mountain,
  MountainSnow,
  Route,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Sunrise,
  Target,
  Telescope,
  Timer,
  Waves,
  Wind,
  Zap,
} from '.';

/**
 * Maps the curated Lucide icon-id strings stored as DATA (`LevelIdentity.iconId`,
 * `BadgeDefinition.iconId`, `BadgeView.iconId`, the icon picker's own value) to
 * the actual re-exported icon components — these ids come from
 * `src/contracts/reward-config.ts` and settings-rewards.md SS4's curated
 * 31-icon set, never from arbitrary strings, so an unmapped id is a real
 * content bug (falls back to a neutral dot glyph rather than crashing, so a
 * bad id degrades visibly instead of taking the screen down).
 *
 * Curated set (settings-rewards.md SS4): flame, zap, star, trophy, medal,
 * award, compass, target, book-open, graduation-cap, languages, dumbbell,
 * heart-pulse, leaf, mountain, mountain-snow, footprints, timer,
 * shield-check, sparkles, sunrise, moon, wine, candy, smartphone, pen-line,
 * music, calendar-check, calendar-range, sliders-horizontal, list-checks.
 * Only entries actually referenced by ratified copy (level arc, starter
 * badges, XP source table) are mapped below — same "map what's used" policy
 * as icons/index.ts itself. Extend this map, not a parallel one, if a future
 * user-created badge needs an icon not yet used by shipped content.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  footprints: Footprints,
  map: Map,
  waves: Waves,
  compass: CompassIcon,
  anchor: Anchor,
  route: Route,
  wind: Wind,
  telescope: Telescope,
  'mountain-snow': MountainSnow,
  mountain: Mountain,
  star: Star,
  'sliders-horizontal': SlidersHorizontal,
  target: Target,
  'list-checks': ListChecks,
  zap: Zap,
  sunrise: Sunrise,
  'calendar-check': CalendarCheck,
  'calendar-range': CalendarRange,
  'calendar-cog': CalendarCog,
  'shield-check': ShieldCheck,
  flame: Flame,
  check: Check,
  timer: Timer,
};

export function RewardIcon({
  iconId,
  size = 20,
  color = 'currentColor',
  strokeWidth,
}: {
  readonly iconId: string;
  readonly size?: number;
  readonly color?: string;
  readonly strokeWidth?: number;
}) {
  const Icon = ICON_MAP[iconId] ?? Star;
  return <Icon size={size} color={color} strokeWidth={strokeWidth} aria-hidden={true} />;
}
